import {MealFirstServiceSsrEnum, MealSecondServiceSsrEnum, MealServiceType} from '@constants/meals.constants';
import {MealSelectionPrice} from '@models/cms/meals.model';
import {createSelector} from '@ngrx/store';
import {selectMealsFeatureState} from '@reducers';
import {selectBooking, selectBookingSsrAvailability, selectCanSellMeals} from './booking.selector';
import {JourneyMeal} from '@models/meals.model';
import {selectMealsCopy, selectPassengerTypesCopy} from './cms.selector';
import {createJourneyMeal, createMealLegs} from '@utils/meals.utils';
import {selectIsPaymentsPage} from "@selectors/router.selector";

export const selectMealSelectionsState = createSelector(
  selectMealsFeatureState,
  (state) => state.mealSelections,
);

export const selectMealSelectionsPriceState = createSelector(
  selectMealsFeatureState,
  (state) => {
    const mealSelectionWithPrices: MealSelectionPrice = {};
    state.mealSelections?.forEach((ms) => {
      /* istanbul ignore else */
      if (ms.legKey) {
        mealSelectionWithPrices[ms.legKey] = {};
        ms.passengers.forEach((p) => {
          const firstServiceSsr = p.ssrs.find(
            (s) => s.serviceType === MealServiceType.FIRST_SERVICE,
          );
          const secondServiceSsr = p.ssrs.find(
            (s) => s.serviceType === MealServiceType.SECOND_SERVICE,
          );
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          mealSelectionWithPrices[ms.legKey!]![p.passengerKey] = {
            firstItem: firstServiceSsr?.ssrCode ?? '',
            firstItemPrice: firstServiceSsr?.price ?? 0,
            firstItemIncluded: firstServiceSsr?.included ?? false,
            firstItemPaid: firstServiceSsr?.paid ?? false,
            secondItem: secondServiceSsr?.ssrCode ?? '',
            secondItemPrice: secondServiceSsr?.price ?? 0,
            secondItemIncluded: secondServiceSsr?.included ?? false,
            secondItemPaid: secondServiceSsr?.paid ?? false,
          };
        });
      }
    });
    return mealSelectionWithPrices;
  },
);

export const selectMealsTotalCostTripSummary = createSelector(
  selectMealsFeatureState,
  (state) => {
    return (
      state.mealSelections
        ?.map((s) =>
          s.passengers.map((p) => p.ssrs.map((s) => s.price)))
        .flat(3) ?? []
    ).reduce((c, a) => (c + a), 0);
  }
);

export const selectMealsTotalCostTripSummaryRounded = createSelector(
  selectMealsTotalCostTripSummary,
  Math.ceil
);

export const selectJurneyMeals = createSelector(
  selectMealsCopy,
  selectBooking,
  selectMealSelectionsState,
  selectBookingSsrAvailability,
  selectPassengerTypesCopy,
  selectCanSellMeals,
  (mealsCopy, booking, mealSelection, ssrAvailability, passengerTypes, canSellMeals) => {
    if (mealsCopy && booking && mealSelection && ssrAvailability && passengerTypes && canSellMeals) {
      const journeyMeals: JourneyMeal[] = [];
      const legs = booking.journeys.flatMap(j => j.segments.flatMap(s => s.legs));
      const mainMealSsrs = Object.values(MealFirstServiceSsrEnum)
      const mainMealLegs = createMealLegs({
        mealSelection,
        ssrAvailability,
        mealSsrCodes: mainMealSsrs,
        passengers: booking.passengers,
        passengerTypes,
        mealServiceType: MealServiceType.FIRST_SERVICE,
        mealService: mealsCopy.mealsSsrs.firstService,
        noMeal: mealsCopy.noMeal,
        canSellMeals,
        journeyLegs: legs
      });

      const mainMeal: JourneyMeal = createJourneyMeal({
        image: mealsCopy.mainMealImage,
        mealAddHtml: mealsCopy.mealAddInnerHTML,
        mealInfo: mealsCopy.mainMealInfo,
        mealLegs: mainMealLegs,
        mealLine1: mealsCopy.mainMealLine1,
        mealLine2: mealsCopy.mainMealLine2,
        mealSsrs: mainMealSsrs,
        mealTitle: mealsCopy.explorersMainMeal,
        serveTime: mealsCopy.servedAfterTakeOff,
        serviceType: MealServiceType.FIRST_SERVICE,
        ssrAvailability: ssrAvailability,
        ssrSellError: mainMealLegs.some(m => !m.canSellMeals),
        buttonAddMeal: mealsCopy.btnAddMainMeal,
        buttonRemoveMeal: mealsCopy.btnRemoveMainMeal
      });
      journeyMeals.push(mainMeal);

      const lightMealSsrs = Object.values(MealSecondServiceSsrEnum);
      const lightMealLegs = createMealLegs({
        mealSelection,
        ssrAvailability,
        mealSsrCodes: lightMealSsrs,
        passengers: booking.passengers,
        passengerTypes,
        mealServiceType: MealServiceType.SECOND_SERVICE,
        mealService: mealsCopy.mealsSsrs.secondService,
        noMeal: mealsCopy.noMeal,
        canSellMeals,
        journeyLegs: legs
      });
      const lightMeal: JourneyMeal = createJourneyMeal({
        image: mealsCopy.lightMealImage,
        mealAddHtml: mealsCopy.mealAddInnerHTML,
        mealInfo: mealsCopy.lightMealInfo,
        mealLegs: lightMealLegs,
        mealLine1: mealsCopy.lightMealLine1,
        mealLine2: mealsCopy.lightMealLine2,
        mealSsrs: lightMealSsrs,
        mealTitle: mealsCopy.explorersLightMeal,
        serveTime: mealsCopy.servedBeforeLanding,
        serviceType: MealServiceType.SECOND_SERVICE,
        ssrAvailability: ssrAvailability,
        ssrSellError: lightMealLegs.some(m => !m.canSellMeals),
        buttonAddMeal: mealsCopy.btnAddLightMeal,
        buttonRemoveMeal: mealsCopy.btnRemoveLightMeal
      });
      journeyMeals.push(lightMeal);

      return journeyMeals;
    }
    return []
  }
);
